import { useMemo, FC, useState } from 'react';
import { Column, CellProps } from 'react-table';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router';
import { useStore } from 'hooks/useRootStore';
import { useMyTranslations } from 'hooks/useMyTranslations';
import { getDate } from 'utils/convertString';
import { PAGE_SIZE, Table } from 'components/Core/Table';
import { NoDataComponent } from 'components/Core/NoData/NoData';
import { AvatarIcon } from 'components/Core/AvatarIcon';
import { TableActionsButtons } from 'components/RequestsComponents/TableActionsButtons';
import RequestsTableFiltersForm from 'components/RequestsComponents/RequestsTableFiltersForm';
import { IPointRequest } from 'stores/requests/types';
import { pointsRequestsTableTranslations } from './translations';
import { PointsRequestsTableTransFields } from './types';
import styles from './pointsRequestsTable.module.scss';

export const PointsRequestsTable: FC = observer(() => {
  const {
    requestsStore: { pointRequests },
  } = useStore();

  const { t } = useMyTranslations<PointsRequestsTableTransFields>([pointsRequestsTableTranslations.name]);

  const navigate = useNavigate();

  const [filtred, setFiltred] = useState<IPointRequest[]>([]);

  const columns: Column<IPointRequest>[] = useMemo(
    () => [
      {
        Header: t('name'),
        id: 'name',
        Cell: ({
          row: {
            original: {
              name,
              id,
              brand: { logoUrl },
            },
          },
        }: CellProps<IPointRequest>) => {
          return (
            <div className={styles.avatar} onClick={() => navigate(`${id}`)}>
              <AvatarIcon avatar={logoUrl} name='logoUrl' size={56} styleProps={{ borderRadius: 8 }} />
              <p className={styles.name}>{name}</p>
            </div>
          );
        },
        accessor: ({ name }) => name,
        width: 2,
      },
      {
        Header: t('date'),
        id: 'date',
        Cell: ({
          row: {
            original: { createdAt },
          },
        }: CellProps<IPointRequest>) => <p className={styles.name}>{getDate(createdAt)}</p>,
        accessor: ({ createdAt }) => +new Date(createdAt),
        width: 1,
      },
      {
        Header: t('brandName'),
        id: 'brandName',
        Cell: ({
          row: {
            original: {
              brand: { name },
            },
          },
        }: CellProps<IPointRequest>) => {
          return (
            <div className={styles.contacts}>
              <span className={styles.contactsPhone}>{name}</span>
            </div>
          );
        },
        accessor: ({ brand: { name } }) => name,
        width: 1,
      },
      {
        Header: t('owner'),
        id: 'owner',
        Cell: ({
          row: {
            original: {
              owner: { name, email },
            },
          },
        }: CellProps<IPointRequest>) => {
          return (
            <div className={styles.contacts}>
              <span className={styles.contactsPhone}>{name}</span>
              <span className={styles.contactsEmail}>{email}</span>
            </div>
          );
        },
        accessor: ({ owner: { name } }) => name,
        width: 1,
      },
      {
        Header: t('description'),
        id: 'description',
        Cell: ({
          row: {
            original: { description },
          },
        }: CellProps<IPointRequest>) => {
          return <p className={styles.description}>{description}</p>;
        },
        accessor: ({ description }) => description,
        width: 1,
      },
      {
        Header: t('address'),
        id: 'address',
        Cell: ({
          row: {
            original: { address },
          },
        }: CellProps<IPointRequest>) => {
          return <p className={styles.description}>{address}</p>;
        },
        accessor: ({ address }) => address,
        width: 2,
      },
      {
        Header: t('option'),
        id: 'option',
        Cell: ({
          row: {
            original: { id, status },
          },
        }: CellProps<IPointRequest>) => <TableActionsButtons id={id} entity='points' status={status} />,
        width: 5,
        disableSortBy: true,
      },
    ],
    [pointRequests]
  );

  columns.forEach((item) => (item.Filter = () => null));

  return pointRequests && pointRequests.length > 0 ? (
    <>
      <RequestsTableFiltersForm data={pointRequests} setFiltredData={setFiltred} />
      <Table columns={columns as never} data={[...filtred]} simpleList isPaginated={pointRequests.length > PAGE_SIZE} />
    </>
  ) : (
    <NoDataComponent />
  );
});
